import {
  CreateGoalConfigurationWithIndividualDatesInput,
  EditGoalConfigurationWithDaysOfWeekInput,
  EditGoalConfigurationWithIndividualDatesInput,
  GoalConfigurationForTableFragment,
  GoalTargetForTableFragment,
} from "apps/web/src/types";
import { z } from "zod";
import { MealPlanDateAssignment } from "../../MealPlans/Content/Calendar/Form/types";
import { IntegerSchema } from "@notemeal/validators";
import { jsDateToDateAndTimeInTz, parseDate } from "@notemeal/shared/ui/utils/dateTimes";
import { CreateGoalConfigurationWithDaysOfWeekInput } from "libs/shared/ui/src/lib/types";

const GoalCategorySchema = z.object(
  {
    id: z.string().min(1),
    name: z.string().min(1),
    defaultEmoji: z.string().min(1),
  },
  { required_error: "Goal category required" }
);
const CompletionCriteriaSchema = z.object({
  markAsComplete: z.boolean({ required_error: "Completion criteria required" }),
  quantity: IntegerSchema.optional(),
  unit: z.string().optional(),
});
const GoalReminderSchema = z.array(
  z.object({ time: z.string().min(1, { message: "Time is required" }), message: z.string().min(1, { message: "Time is required" }) })
);
export const GoalConfigurationFormSchema = z.object({
  task: z.string({ required_error: "Task is required" }).min(1, { message: "Task is required" }),
  reminders: GoalReminderSchema,
  timezone: z.string().min(1, { message: "Timezone is required" }),
  type: GoalCategorySchema,
  completionCriteria: CompletionCriteriaSchema,
});

export type GoalConfigurationFormType = z.infer<typeof GoalConfigurationFormSchema>;

export const goalConfigurationFormDefaultValues = (
  goalConfiguration?: GoalConfigurationForTableFragment
): Partial<GoalConfigurationFormType> => {
  if (goalConfiguration) {
    return {
      task: goalConfiguration.name,
      type: {
        id: goalConfiguration.category.id,
        name: goalConfiguration.category.name,
        defaultEmoji: goalConfiguration.category.defaultEmoji,
      },
      completionCriteria: {
        markAsComplete: !goalConfiguration.target,
        quantity: goalConfiguration.target?.value,
        unit: goalConfiguration.target?.unit,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      reminders: goalConfiguration.reminders.map(r => ({ time: r.time, message: r.message })),
    };
  }
  return {
    task: "",
    type: undefined,
    completionCriteria: undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    reminders: [],
  };
};

export type CreateGoalConfigurationInputType = CreateGoalConfigurationWithDaysOfWeekInput | CreateGoalConfigurationWithIndividualDatesInput;

export const goalConfigurationFormToCreateInput = (
  athleteId: string,
  goal: GoalConfigurationFormType,
  dateAssignment: MealPlanDateAssignment
): CreateGoalConfigurationInputType => {
  const usesDayOfWeek = dateAssignment.mode === "weekly";
  if (usesDayOfWeek) {
    return {
      input: {
        athleteId,
        categoryId: goal.type.id,
        emoji: goal.type.defaultEmoji,
        ...goalConfigurationFormToInputCommon(goal, dateAssignment),
        target: getTargetInput(goal, null).target,
      },
      dayOfWeekAssignments: dateAssignment.dayOfWeekPriorities.map(dayOfWeek => dayOfWeek.dayOfWeek),
      startDate: dateAssignment.startDate,
      //todo fix state to not allow null
      endDate: dateAssignment.endDate!,
    };
  } else {
    return {
      input: {
        athleteId,
        categoryId: goal.type.id,
        emoji: goal.type.defaultEmoji,
        ...goalConfigurationFormToInputCommon(goal, dateAssignment),
        target: getTargetInput(goal, null).target,
      },
      individualDates: dateAssignment.individualDates,
    };
  }
};

// export type EditGoalConfigurationInputType =
//   | { data: EditGoalConfigurationWithDaysOfWeekInput; inputType: "daysOfWeek" }
//   | { data: EditGoalConfigurationWithIndividualDatesInput; inputType: "individualDates" };
export type EditGoalConfigurationInputType = EditGoalConfigurationWithDaysOfWeekInput | EditGoalConfigurationWithIndividualDatesInput;

export const goalConfigurationFormToEditInput = (
  goalId: string,
  goal: GoalConfigurationFormType,
  dateAssignment: MealPlanDateAssignment,
  loadedIndividualDates: string[],
  previousTarget: GoalTargetForTableFragment | null
): EditGoalConfigurationInputType => {
  const usesDayOfWeek = dateAssignment.mode === "weekly";
  const removeIndividualDates = usesDayOfWeek
    ? null
    : loadedIndividualDates.length
    ? loadedIndividualDates.filter(date => !dateAssignment.individualDates.includes(date))
    : [];
  const addIndividualDates = usesDayOfWeek
    ? null
    : dateAssignment.individualDates.length
    ? dateAssignment.individualDates.filter(date => !loadedIndividualDates.includes(date))
    : [];
  if (usesDayOfWeek) {
    return {
      // data: {
      input: {
        id: goalId,
        ...goalConfigurationFormToInputCommon(goal, dateAssignment),
        ...getTargetInput(goal, previousTarget),
      },
      dayOfWeekAssignments: dateAssignment.dayOfWeekPriorities.map(dayOfWeek => dayOfWeek.dayOfWeek),
      startDate: dateAssignment.startDate,
      //todo fix schema to not allow null
      endDate: dateAssignment.endDate!,
      // },
      // inputType: "daysOfWeek",
    };
  } else {
    return {
      // data: {
      input: {
        id: goalId,
        ...goalConfigurationFormToInputCommon(goal, dateAssignment),
        ...getTargetInput(goal, previousTarget),
      },
      addIndividualDates: addIndividualDates || [],
      removeIndividualDates: removeIndividualDates || [],
      // },
      // inputType: "individualDates",
    };
  }
};

const goalConfigurationFormToInputCommon = (goal: GoalConfigurationFormType, dateAssignment: MealPlanDateAssignment) => {
  return {
    name: goal.task,
    timezone: goal.timezone,
    reminders: goal.reminders,
  };
};

const getTargetInput = (
  goal: GoalConfigurationFormType,
  previousTarget: GoalTargetForTableFragment | null
): { removeTarget: boolean; target: { value: number; unit: string } | null } => {
  if (previousTarget) {
    if (goal.completionCriteria.markAsComplete) {
      return { target: null, removeTarget: true };
    } else if (previousTarget.unit === goal.completionCriteria.unit && previousTarget.value === goal.completionCriteria.quantity) {
      //target unchanged
      return { target: null, removeTarget: false };
    } else if (goal.completionCriteria.quantity && goal.completionCriteria.unit) {
      //target has been updated
      return { target: { value: goal.completionCriteria.quantity, unit: goal.completionCriteria.unit }, removeTarget: false };
    } else {
      return { target: null, removeTarget: false };
    }
  } else if (goal.completionCriteria.markAsComplete) {
    return { target: null, removeTarget: false };
  } else if (goal.completionCriteria.quantity && goal.completionCriteria.unit) {
    return { target: { value: goal.completionCriteria.quantity, unit: goal.completionCriteria.unit }, removeTarget: false };
  } else {
    return { target: null, removeTarget: false };
  }
};

export const getDateAssignmentStateFromGoalConfiguration = (goal: GoalConfigurationForTableFragment): MealPlanDateAssignment => {
  const startDate = goal.startDate ?? jsDateToDateAndTimeInTz(new Date(), goal.timezone).date;
  const dateAssignmentBase = {
    startDate,
    startDateRaw: parseDate(startDate),
    endDate: goal.endDate,
    endDateRaw: goal.endDate ? new Date(goal.endDate) : null,
    disableEndDate: false,
  };

  if (goal.individualDates.length > 0) {
    return {
      mode: "individual",
      dayOfWeekPriorities: [],
      individualDates: goal.individualDates,
      ...dateAssignmentBase,
    };
  } else {
    return {
      mode: "weekly",
      dayOfWeekPriorities: goal.dayOfWeekAssignments.map(dayOfWeek => ({ dayOfWeek, priority: 0 })),
      individualDates: goal.individualDates,
      ...dateAssignmentBase,
    };
  }
};
